import { useReducer, useCallback, useMemo, useRef } from 'react';

import { getCouponsConfig } from '../config';
import { COUPON_COOKIE_VERSION } from '../constants';
import { getDiscountTypeAndAmount, isReferrerValid } from '../utils/discount';
import {
  getCoupon,
  REFERRAL_NOT_FOUND_RESPONSE_DATA,
} from '../utils/getCoupon';
import * as couponCookie from '../utils/couponCookie';

import type { ReferrerResponse, GetCouponArgs } from '../utils/getCoupon';
import type { DiscountTypeAndAmount, ReferrerType } from '../types';
import type {
  PromotionContextValue,
  PromotionState,
  SetPromotionArgs,
} from './types';

async function fetchValidatedCoupon(
  args: GetCouponArgs
): Promise<ReferrerResponse> {
  const response = await getCoupon(args);

  if (response === REFERRAL_NOT_FOUND_RESPONSE_DATA) {
    return response;
  }

  if (typeof response !== 'object' || !('id' in response)) {
    throw new Error('Unexpected referrer response');
  }

  return response;
}

type PromotionAction =
  | {
      type: 'COMMIT';
      code: string;
      referrerType: ReferrerType;
      associatedAt: string;
      response: ReferrerResponse;
    }
  | { type: 'EXPIRE' }
  | { type: 'ERROR'; error: Error }
  | { type: 'LOADING'; loading: boolean }
  | { type: 'RESET' };

function createReducer(defaultPromotion: DiscountTypeAndAmount): {
  reducer: (state: PromotionState, action: PromotionAction) => PromotionState;
  initialState: PromotionState;
} {
  const initialState: PromotionState = {
    couponCode: undefined,
    referrerType: undefined,
    referrerAssociatedAt: undefined,
    coupon: undefined,
    isValid: false,
    discountAmount: defaultPromotion.discountAmount,
    discountType: defaultPromotion.discountType,
    loading: true,
    error: undefined,
    isExpired: false,
  };

  function reducer(
    state: PromotionState,
    action: PromotionAction
  ): PromotionState {
    switch (action.type) {
      case 'COMMIT': {
        const { discountAmount, discountType } = getDiscountTypeAndAmount({
          coupon: action.response,
          defaultDiscount: defaultPromotion,
        });
        return {
          ...state,
          couponCode: action.code,
          referrerType: action.referrerType,
          referrerAssociatedAt: action.associatedAt,
          coupon: action.response,
          isValid: isReferrerValid(action.response),
          discountAmount,
          discountType,
          loading: false,
          error: undefined,
          isExpired: false,
        };
      }
      case 'EXPIRE':
        return { ...state, isExpired: true, loading: false };
      case 'ERROR':
        return { ...state, error: action.error, loading: false };
      case 'LOADING':
        return { ...state, loading: action.loading };
      case 'RESET':
        return { ...initialState, loading: false };
    }
  }

  return { reducer, initialState };
}

export function usePromotionState(
  defaultPromotion: DiscountTypeAndAmount
): PromotionContextValue {
  const { reducer, initialState } = useMemo(
    () => createReducer(defaultPromotion),
    [defaultPromotion]
  );
  const [state, dispatch] = useReducer(reducer, initialState);
  const callIdRef = useRef(0);

  const setPromotion = useCallback(
    async ({
      code,
      referrerType: type,
      associatedAt: providedAssociatedAt,
      skipCookieWrites = false,
    }: SetPromotionArgs) => {
      const associatedAt =
        typeof providedAssociatedAt === 'string'
          ? providedAssociatedAt
          : (providedAssociatedAt ?? new Date()).toISOString();
      const thisCallId = ++callIdRef.current;

      dispatch({ type: 'LOADING', loading: true });

      const { apiUrl } = getCouponsConfig();

      try {
        const response = await fetchValidatedCoupon({
          apiUrl,
          coupon: { code, type },
        });

        if (callIdRef.current !== thisCallId) {
          return;
        }

        if (isReferrerValid(response)) {
          dispatch({
            type: 'COMMIT',
            code,
            referrerType: type,
            associatedAt,
            response,
          });
          if (!skipCookieWrites) {
            couponCookie.set({
              referrerCode: code,
              referrerType: type,
              referrerAssociatedAt: associatedAt,
              version: COUPON_COOKIE_VERSION,
            });
          }
        } else {
          dispatch({ type: 'RESET' });
          if (!skipCookieWrites) {
            couponCookie.remove();
          }
        }
      } catch (caughtError: unknown) {
        if (callIdRef.current !== thisCallId) {
          return;
        }

        const nextError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Error fetching coupon');
        dispatch({ type: 'RESET' });
        dispatch({ type: 'ERROR', error: nextError });
        if (!skipCookieWrites) {
          couponCookie.remove();
        }
      }
    },
    [dispatch]
  );

  const clearPromotion = useCallback(
    (skipCookieWrites = false) => {
      dispatch({ type: 'RESET' });
      if (!skipCookieWrites) {
        couponCookie.remove();
      }
    },
    [dispatch]
  );

  return useMemo(
    () => ({ ...state, setPromotion, clearPromotion }),
    [state, setPromotion, clearPromotion]
  );
}
